<template>
  <div v-if="recordChanged" class="draft-banner" role="alert" data-testid="draft-banner-record-changed">
    <div class="draft-banner__text">
      <LumiIcon name="reply" class="draft-banner__icon" />
      <span>
        <span class="draft-banner__title">{{ t('formDrafts.recordChangedTitle') }}</span>
        <span class="draft-banner__meta">{{ t('errors.recordChangedReload') }}</span>
      </span>
    </div>
    <div class="draft-banner__actions">
      <Button size="sm" variant="soft" @click="$emit('reload')">
        {{ t('formDrafts.reload') }}
      </Button>
    </div>
  </div>
  <div v-else class="draft-banner" role="status">
    <div class="draft-banner__text">
      <LumiIcon name="reply" class="draft-banner__icon" />
      <span>
        <span class="draft-banner__title">{{ t('formDrafts.bannerTitle') }}</span>
        <span v-if="savedAt" class="draft-banner__meta">{{ t('formDrafts.bannerSavedAt', { when: savedAtLabel }) }}</span>
        <span v-if="recordChangedSinceDraft" class="draft-banner__line" data-testid="draft-banner-base-moved">
          {{ t('formDrafts.bannerRecordChanged') }}
        </span>
      </span>
    </div>
    <div class="draft-banner__actions">
      <Button size="sm" variant="secondary" @click="$emit('discard')">
        {{ t('formDrafts.discard') }}
      </Button>
      <Button size="sm" variant="soft" @click="$emit('restore')">
        {{ t('formDrafts.restore') }}
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Button, LumiIcon } from '@lumi/ui';

// Restore/discard for a browser form draft (utils/formDraftStorage.ts). The two
// actions are always explicit: nothing in this feature restores a draft on its
// own, because resurrecting old input into a form somebody is about to save is
// how wrong data gets written. formDrafts.* is shared app-kit vocabulary.
//
// Two states (ADR 0101). `recordChanged`: the last save was refused because
// somebody else changed the record meanwhile; the input is parked as a draft and
// the only action is Reload (the host re-reads the record and calls
// `noteFormReset()`), after which the banner offers that draft. Otherwise the
// draft state, with one more line when the draft was taken against an older
// version of the record (`recordChangedSinceDraft`): Restore then puts back only
// the fields the user changed. A host renders the banner when
// `hasDraft || recordChanged`.
const props = defineProps<{
  savedAt?: number | null;
  recordChanged?: boolean;
  recordChangedSinceDraft?: boolean;
}>();
defineEmits<{ (e: 'restore'): void; (e: 'discard'): void; (e: 'reload'): void }>();

const { t, locale } = useI18n();

const savedAtLabel = computed(() => {
  if (!props.savedAt) return '';
  try {
    return new Date(props.savedAt).toLocaleString(locale.value, {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return new Date(props.savedAt).toISOString();
  }
});
</script>

<style scoped>
/* Tokens only (no tailwind utilities): the module SPAs do not all process the
   accent-* utility classes — same constraint as DemoStageBar. */
.draft-banner {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.5rem 0.75rem;
  margin-bottom: 1rem;
  border: 1px solid var(--accent-soft-2);
  border-radius: var(--radius-md);
  background: var(--accent-soft);
  color: var(--accent-text);
}
.draft-banner__text {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
  font-size: var(--text-sm);
}
.draft-banner__icon {
  width: 1.1rem;
  height: 1.1rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
}
.draft-banner__title {
  font-weight: 500;
}
.draft-banner__meta {
  margin-left: 0.375rem;
  opacity: 0.85;
}
.draft-banner__line {
  display: block;
  margin-top: 0.125rem;
}
.draft-banner__actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}
</style>
